import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useAmplitude from 'utils/hooks/useAmplitude';
import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import { WALLET_STATUS } from 'constants/wallet';
import { useFreezeWallet, useCloseWallet } from 'services/reactQueryHooks/wallet';
import { getWalletSupportPhoneNumber } from './helper';

// `balanceMajor` and `currency` are the wallet's, for the freeze/close analytics events.
const useFooterPage = ({ status, balanceMajor, currency, onStatusChange } = {}) => {
  const { sendAmplitudeEvent } = useAmplitude();
  const walletSnapshot = { walletBalance: balanceMajor, currency };
  const { mutate: freezeWallet, isLoading: isFreezeLoading } = useFreezeWallet(walletSnapshot);
  const { mutate: closeWallet, isLoading: isCloseLoading } = useCloseWallet(walletSnapshot);
  const country = useSelector(state => state.user.currentCorporation?.country);
  const supportPhoneNumber = getWalletSupportPhoneNumber(country);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  // Seeded from the wallet's server status; the freeze/close handlers below still flip these
  // optimistically because their endpoints are mocked (see the TODO in reactQueryHooks/wallet.js).
  // They also report the change upwards (`onStatusChange`) so the whole dashboard follows.
  const [isWalletFrozen, setIsWalletFrozen] = useState(status === WALLET_STATUS.frozen);
  const [isWalletClosed, setIsWalletClosed] = useState(status === WALLET_STATUS.closed);

  useEffect(() => {
    if (status) {
      setIsWalletFrozen(status === WALLET_STATUS.frozen);
      setIsWalletClosed(status === WALLET_STATUS.closed);
    }
  }, [status]);
  const [isFreezeModalVisible, setIsFreezeModalVisible] = useState(false);
  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
  // The close modal's "Wallet closed" confirmation screen — separate from the wallet being closed,
  // so dismissing the modal does not make the wallet look open again.
  const [isCloseDone, setIsCloseDone] = useState(false);
  const [refundNote, setRefundNote] = useState('');

  const handleToggleDetails = () => {
    setIsDetailsOpen(isOpen => !isOpen);
  };

  const handleOpenActionModal = () => {
    if (isWalletFrozen) {
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCloseButtonClicked);
      setIsCloseModalVisible(true);
    } else {
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletFreezeButtonClicked);
      setIsFreezeModalVisible(true);
    }
  };

  const handleCancelFreezeModal = () => {
    setIsFreezeModalVisible(false);
  };

  const handleConfirmFreeze = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletFreezeConfirmButtonClicked);
    freezeWallet(undefined, {
      onSuccess: result => {
        setIsWalletFrozen(true);
        setIsFreezeModalVisible(false);
        onStatusChange?.({
          status: WALLET_STATUS.frozen,
          statusChangedAt: result?.statusChangedAt ?? null,
        });
      },
    });
  };

  const handleCancelCloseModal = () => {
    setIsCloseModalVisible(false);
    setIsCloseDone(false);
    setRefundNote('');
  };

  const handleConfirmClose = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCloseConfirmButtonClicked);
    closeWallet(refundNote, {
      onSuccess: result => {
        setIsCloseDone(true);
        setIsWalletClosed(true);
        onStatusChange?.({
          status: WALLET_STATUS.closed,
          statusChangedAt: result?.statusChangedAt ?? null,
        });
      },
    });
  };

  return {
    isDetailsOpen,
    isWalletFrozen,
    isWalletClosed,
    isCloseDone,
    isFreezeModalVisible,
    isCloseModalVisible,
    isFreezeLoading,
    isCloseLoading,
    refundNote,
    supportPhoneNumber,
    onToggleDetails: handleToggleDetails,
    onOpenActionModal: handleOpenActionModal,
    onCancelFreezeModal: handleCancelFreezeModal,
    onConfirmFreeze: handleConfirmFreeze,
    onCancelCloseModal: handleCancelCloseModal,
    onConfirmClose: handleConfirmClose,
    onRefundNoteChange: setRefundNote,
  };
};

export default useFooterPage;
